/**
 * API Configuration for Admin Frontend
 *
 * Centralized configuration for API endpoints and authentication.
 * When running locally (npm start), we default to the dev environment unless
 * REACT_APP_API_BASE_URL is set. Set REACT_APP_DEV_API_BASE_URL or
 * REACT_APP_API_BASE_URL in .env.development.local from your dev stack:
 *   cd infrastructure && terraform output -raw api_base_url
 */

const isDevelopment = process.env.NODE_ENV === 'development';

// Dev API base URL when running locally. Override via REACT_APP_DEV_API_BASE_URL
// or REACT_APP_API_BASE_URL (from e.g. terraform output -raw api_base_url in dev stack).
const DEV_DEFAULT_API_BASE_URL =
  process.env.REACT_APP_DEV_API_BASE_URL ||
  'https://your-dev-api-id.execute-api.us-east-2.amazonaws.com';

// API Base URL: explicit env wins; in development default to dev, else prod
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (isDevelopment ? DEV_DEFAULT_API_BASE_URL : 'https://api.stagespotter.com');

/**
 * Creates headers for API requests with JWT authentication
 */
export function getApiHeaders(token: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

/**
 * Makes an authenticated API request with JWT token
 */
export async function apiRequest<T>(
  endpoint: string,
  token: string,
  options?: RequestInit
): Promise<T> {
  const url = endpoint.startsWith('http') 
    ? endpoint 
    : `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      ...getApiHeaders(token),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorMessage;
    } catch {
      // If not JSON, use the text as-is
      if (errorText) errorMessage = errorText;
    }
    throw new Error(errorMessage);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

